// OWNED BY the calls implementation agent (see ../CallsTab.tsx ownership note).
//
// Data layer of the customer-page calls tab (שיחות). Ports the legacy calls
// query legwork (src/utils/Haim/webotDataHelpers.ts + CallsListPage/api/calls.ts
// + useCallsData.ts) into a lean, contact-scoped, cache-backed module:
//
// - Accessible virtual numbers = own userSettings.virtualNumbers, plus (when the
//   user is an org admin) every team member's virtualNumbers — the FE mirror of
//   the server's getAccessibleVirtualNumbersForUser semantics, same resolution
//   the legacy useCallsData/getMyTeamData flow performed.
// - Calls for a contact = per virtual number:
//   payCall-settings/{vn}/calls-summary where callerNumberInternational == contact.id
//   (contact doc id === E.164 phone === the call join key). Queries run in
//   PARALLEL (the legacy page walked members sequentially with sleep(100)).
//   The `where == + orderBy createdTime desc` shape is covered by the existing
//   composite index (callerNumberInternational ASC, createdTime DESC).
// - Transcript/summary = lazy per call from the nested
//   payCall-settings/{vn}/calls-summary/{id}/enrichment/transcriptAndSummary doc.
//
// Everything flows through the session cache so the badge, preload (contacts
// table row hover) and the tab body share one warm dataset.

import { db, firebaseApp } from "firebaseConfig";
import { GenericConverter } from "utils/firestoreConverters";
import { getAudioUrlFromCallSummary } from "utils/calls/display";
import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { fetchCached, setCached } from "../../data/sessionCache";
import type {
  CallSummary,
  CallEndResult,
  RealEstateContact,
  CallTranscriptAndSummary
} from "../../../../../../@types/sharedSchema";

// ---------------------------------------------------------------------------
// Cache keys (shared-key convention: "<tabId>:<contact.id>" + suffixes)
// ---------------------------------------------------------------------------

export const contactCallsKey = (contactId: string): string => `calls:${contactId}`;

export const callTranscriptKey = (contactId: string, call: CallSummary): string =>
  `calls:${contactId}:transcript:${call.virtualNumber}:${call.id}`;

const accessibleVnsKey = (email: string): string => `calls:accessible-vns:${email}`;

// SWR window for the calls list. In practice this governs the row-hover
// preload: once the tab is open, the per-VN onSnapshot listeners keep the key
// fresh (their setCached bumps fetchedAt). 5 min keeps table skimming from
// re-firing the per-virtual-number query fan-out on every re-hover — that
// fan-out is expensive for org admins with many accessible numbers.
export const CALLS_MAX_AGE_MS = 5 * 60_000;
// A missing transcript may appear later (transcription pipeline / backfill).
// NOTE: useSessionCacheValue fetches once per mount, so a MOUNTED row never
// revalidates on its own — this max-age only kicks in when the row remounts
// (filter-pill toggle, "show more" repaging, tab/page reopen), which then
// refetches entries older than this instead of serving a cached null forever.
export const TRANSCRIPT_MAX_AGE_MS = 5 * 60_000;
const ACCESSIBLE_VNS_MAX_AGE_MS = 10 * 60_000;

// Per-contact call history is small; the limit is a safety valve, not paging.
const MAX_CALLS_PER_VIRTUAL_NUMBER = 250;

// ---------------------------------------------------------------------------
// Accessible virtual numbers (FE side of getAccessibleVirtualNumbersForUser)
// ---------------------------------------------------------------------------

type OrgDocShape = { admins?: string[]; users?: string[] };

async function resolveAccessibleVirtualNumbers(email: string): Promise<string[]> {
  const ownDoc = await db.doc(`dashboard-users-v2/${email}`).get();
  const ownNumbers = (ownDoc.data()?.virtualNumbers as string[] | undefined) ?? [];

  let teamNumbers: string[] = [];

  try {
    // Same org resolution as FirestoreContext: first org whose users contain me.
    const orgsSnapshot = await db
      .collection("dashboard-orgs")
      .where("users", "array-contains", email)
      .get();

    const org = orgsSnapshot.docs[0]?.data() as OrgDocShape | undefined;
    const isOrgAdmin = org?.admins?.includes(email) ?? false;

    if (isOrgAdmin && org?.users?.length) {
      const teammates = org.users.filter((teamMail) => teamMail && teamMail !== email);
      const teammateDocs = await Promise.all(
        teammates.map((teamMail) => db.doc(`dashboard-users-v2/${teamMail}`).get())
      );
      teamNumbers = teammateDocs.flatMap(
        (doc) => (doc.data()?.virtualNumbers as string[] | undefined) ?? []
      );
    }
  } catch (error) {
    // Org lookup is best-effort — fall back to own numbers only (a non-admin
    // has no org-wide visibility anyway).
    console.error("Calls tab: failed resolving org virtual numbers", error);
  }

  return Array.from(new Set([...ownNumbers, ...teamNumbers].filter(Boolean)));
}

export async function getAccessibleVirtualNumbers(): Promise<string[]> {
  const email = firebaseApp.auth().currentUser?.email;
  if (!email) return [];
  return fetchCached(
    accessibleVnsKey(email),
    () => resolveAccessibleVirtualNumbers(email),
    { maxAgeMs: ACCESSIBLE_VNS_MAX_AGE_MS }
  );
}

// ---------------------------------------------------------------------------
// Calls for a contact
// ---------------------------------------------------------------------------

const callsQueryForVirtualNumber = (virtualNumber: string, contactId: string) =>
  db
    .collection(`payCall-settings/${virtualNumber}/calls-summary`)
    .withConverter(GenericConverter<CallSummary>())
    .where("callerNumberInternational", "==", contactId)
    .orderBy("createdTime", "desc")
    .limit(MAX_CALLS_PER_VIRTUAL_NUMBER);

export function getCallTimeMs(call: CallSummary): number {
  if (call.startAt) {
    const parsed = Date.parse(call.startAt);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (call.createdTime instanceof Date) return call.createdTime.getTime();
  return 0;
}

const sortCallsNewestFirst = (calls: CallSummary[]): CallSummary[] =>
  [...calls].sort((a, b) => getCallTimeMs(b) - getCallTimeMs(a));

const mapCallDocs = (
  docs: Array<{ id: string; data: () => CallSummary }>,
  virtualNumber: string
): CallSummary[] =>
  docs.map((doc) => {
    const data = doc.data();
    return { ...data, id: doc.id, virtualNumber: data.virtualNumber || virtualNumber };
  });

/**
 * One-shot fetch of all calls with this contact across every accessible virtual
 * number. Per-number failures (e.g. Firestore rules denying a teammate's
 * subcollection) are tolerated and drop only that number's calls — the legacy
 * flow failed the whole batch in that case.
 */
export async function fetchCallsForContact(contactId: string): Promise<CallSummary[]> {
  if (!contactId) return [];

  const virtualNumbers = await getAccessibleVirtualNumbers();

  const perNumber = await Promise.all(
    virtualNumbers.map(async (virtualNumber) => {
      try {
        const snapshot = await callsQueryForVirtualNumber(virtualNumber, contactId).get();
        return mapCallDocs(snapshot.docs, virtualNumber);
      } catch (error) {
        console.error(`Calls tab: failed loading calls of ${virtualNumber}`, error);
        return [] as CallSummary[];
      }
    })
  );

  return sortCallsNewestFirst(perNumber.flat());
}

/** Fire-and-forget warm-up used by preloadCallsTab (row hover / page mount). */
export function preloadContactCalls(contact: RealEstateContact | null | undefined): void {
  const contactId = contact?.id;
  if (!contactId) return;
  void fetchCached(contactCallsKey(contactId), () => fetchCallsForContact(contactId), {
    maxAgeMs: CALLS_MAX_AGE_MS
  }).catch(() => undefined);
}

/**
 * Live listeners: one light onSnapshot per accessible virtual number, filtered
 * to this contact. The merged (sorted) result is written to the session cache,
 * so the tab body AND the tab-strip badge update in real time when a new call
 * lands. To avoid clobbering the cached full list with a partial one, nothing
 * is written until every listener delivered its first snapshot.
 */
export function subscribeToContactCalls(contactId: string): () => void {
  if (!contactId) return () => undefined;

  let cancelled = false;
  const unsubscribers: Array<() => void> = [];

  void getAccessibleVirtualNumbers()
    .then((virtualNumbers) => {
      if (cancelled || !virtualNumbers.length) return;

      const callsByNumber = new Map<string, CallSummary[]>();
      const pendingFirstSnapshot = new Set(virtualNumbers);

      const writeMergedIfReady = (): void => {
        if (pendingFirstSnapshot.size > 0 || cancelled) return;
        const merged = sortCallsNewestFirst(Array.from(callsByNumber.values()).flat());
        setCached(contactCallsKey(contactId), merged);
      };

      virtualNumbers.forEach((virtualNumber) => {
        const unsubscribe = callsQueryForVirtualNumber(virtualNumber, contactId).onSnapshot(
          (snapshot) => {
            callsByNumber.set(virtualNumber, mapCallDocs(snapshot.docs, virtualNumber));
            pendingFirstSnapshot.delete(virtualNumber);
            writeMergedIfReady();
          },
          (error) => {
            // Tolerate per-number listener failures (permissions etc.) exactly
            // like the one-shot fetch — that number contributes no calls.
            // Firestore terminates an errored listener permanently, so if it
            // already delivered data (mid-session permission/token failure),
            // KEEP the last-known-good calls instead of blanking them out of
            // the open list and badge.
            console.error(`Calls tab: listener failed for ${virtualNumber}`, error);
            if (!callsByNumber.has(virtualNumber)) callsByNumber.set(virtualNumber, []);
            pendingFirstSnapshot.delete(virtualNumber);
            writeMergedIfReady();
          }
        );
        unsubscribers.push(unsubscribe);
      });
    })
    .catch(() => undefined);

  return () => {
    cancelled = true;
    unsubscribers.forEach((unsubscribe) => unsubscribe());
  };
}

// ---------------------------------------------------------------------------
// Transcript / AI summary (lazy per call)
// ---------------------------------------------------------------------------

export async function fetchCallTranscript(
  call: CallSummary
): Promise<CallTranscriptAndSummary | null> {
  if (!call.virtualNumber || !call.id) return null;
  const snapshot = await db
    .collection(`payCall-settings/${call.virtualNumber}/calls-summary/${call.id}/enrichment`)
    .doc("transcriptAndSummary")
    .withConverter(GenericConverter<CallTranscriptAndSummary>())
    .get();
  return snapshot.data() ?? null;
}

// ---------------------------------------------------------------------------
// Classification, filters, formatting (pure helpers)
// ---------------------------------------------------------------------------

/** Missed-call convention across the app: any non-ANSWER result. */
export const isMissedCall = (call: CallSummary): boolean => call.result !== "ANSWER";

export type CallsFilterId = "all" | "incoming" | "outgoing" | "missed";

export type CallKind = Exclude<CallsFilterId, "all">;

/**
 * Mutually-exclusive partition used by the filter pills (mock s24): missed
 * first (regardless of direction), then answered by direction. Legacy docs
 * without callDirection default to inbound (customers call in — the historical
 * default of the ingestion providers).
 */
export function classifyCall(call: CallSummary): CallKind {
  if (isMissedCall(call)) return "missed";
  return call.callDirection === "outbound" ? "outgoing" : "incoming";
}

export function filterCallsByKind(calls: CallSummary[], filter: CallsFilterId): CallSummary[] {
  if (filter === "all") return calls;
  return calls.filter((call) => classifyCall(call) === filter);
}

export function countCallsByKind(calls: CallSummary[]): Record<CallsFilterId, number> {
  const counts: Record<CallsFilterId, number> = {
    all: calls.length,
    incoming: 0,
    outgoing: 0,
    missed: 0
  };
  calls.forEach((call) => {
    counts[classifyCall(call)] += 1;
  });
  return counts;
}

/** duration is a STRING of seconds on the call doc — parse before formatting. */
export const callDurationSeconds = (call: CallSummary): number => {
  const parsed = parseInt(call.duration, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

/** "4:12" style (h:mm:ss over an hour) — matches the mock, unlike HH:MM:SS. */
export function formatCallDuration(totalSeconds: number): string {
  const safeSeconds = Number.isFinite(totalSeconds) && totalSeconds > 0 ? totalSeconds : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = Math.floor(safeSeconds % 60);
  const paddedSeconds = seconds.toString().padStart(2, "0");
  if (hours > 0) return `${hours}:${minutes.toString().padStart(2, "0")}:${paddedSeconds}`;
  return `${minutes}:${paddedSeconds}`;
}

const isSameCalendarDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "היום 09:12" / "אתמול 18:20" / "5.7 · 10:20" (+ short year off-year). */
export function formatCallWhen(call: CallSummary): string {
  const timeMs = getCallTimeMs(call);
  if (!timeMs) return "";

  const date = new Date(timeMs);
  const now = new Date();
  const time = `${date.getHours().toString().padStart(2, "0")}:${date
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  if (isSameCalendarDay(date, now)) return `היום ${time}`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return `אתמול ${time}`;

  const sameYear = date.getFullYear() === now.getFullYear();
  const dayMonth = `${date.getDate()}.${date.getMonth() + 1}`;
  const dateLabel = sameYear ? dayMonth : `${dayMonth}.${date.getFullYear() % 100}`;
  return `${dateLabel} · ${time}`;
}

export function missedCallResultText(result: CallEndResult | undefined): string {
  switch (result) {
    case "BUSY":
      return "תפוס";
    case "CANCEL":
      return "השיחה בוטלה";
    case "IVR_HANGUP":
      return "נותקה בנתב השיחות";
    case "CONGESTION":
      return "הקו היה עמוס";
    case "NOANSWER":
    default:
      return "אין מענה";
  }
}

/** Card-header caption, e.g. "4 שיחות · 13 דקות מוקלטות". */
export function getCallsHeaderCaption(calls: CallSummary[]): string {
  const total = calls.length;
  const totalLabel = total === 1 ? "שיחה אחת" : `${total} שיחות`;

  const recordedSeconds = calls
    .filter((call) => !isMissedCall(call))
    .reduce((acc, call) => acc + callDurationSeconds(call), 0);
  const recordedMinutes = Math.round(recordedSeconds / 60);

  if (recordedMinutes <= 0) return totalLabel;
  const minutesLabel = recordedMinutes === 1 ? "דקה מוקלטת" : `${recordedMinutes} דקות מוקלטות`;
  return `${totalLabel} · ${minutesLabel}`;
}

/**
 * In-page playback URL candidates, ordered: the app-wide replica-vs-provider
 * pick first (getAudioUrlFromCallSummary — Firebase replica when createdTime >
 * 2024-09-30, else the direct provider link; kept in sync with the server's
 * resolveRecordingUrl), then the remaining links as onError fallbacks.
 * Downloads do NOT use these — they go through GET /api/v1/calls/recording/download.
 */
export function getCallAudioUrlCandidates(call: CallSummary): string[] {
  const primary = getAudioUrlFromCallSummary(call);
  const candidates = [
    primary,
    call.recordingLinkReplicatedToFirebaseUrl,
    call.recordingLinkDirect,
    call.recordingLink
  ];
  return Array.from(new Set(candidates.filter((url): url is string => Boolean(url))));
}

export function contactDisplayName(contact: RealEstateContact): string {
  const fullName = [contact.callerFirstName, contact.callerLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (fullName) return fullName;
  return parsePhoneNumber(contact.id)?.formatNational() ?? contact.id ?? "";
}
